import { useCancelSubscription } from '@givebacks/api/store';
import { useQueryClient } from '@tanstack/react-query';
import { centsToDollar } from 'core/Utils';
import {
  ButtonMantine,
  Card,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';

import { useCrowdfundingProvider } from '../../../../../Context';
import { Subscription } from '../../../../../api';
import { storeKeys } from '../../../../../api/query-key-factory';

type CancelSubscriptionModalProps = {
  opened: boolean;
  onClose: () => void;
  subscription: Subscription;
};

type CancelSubscriptionModalContentProps = {
  onClose: () => void;
  subscription: Subscription;
};

const RecurringDonationInfoCard = ({ subscription }: { subscription: Subscription }) => {
  return (
    <Card bg="cobalt.0" radius="sm">
      <Text type="sub" fw={600} mb={8}>
        Donor Info
      </Text>
      <SimpleGrid cols={2} spacing={4}>
        <Text fz="xs" c="gray">
          <Text span fw={600} w="40px" display="inline-block">
            Name:
          </Text>{' '}
          {subscription.donor_name}
        </Text>
        <Text fz="xs" c="gray">
          <Text span fw={600} display="inline-block">
            Donation Type:
          </Text>{' '}
          Recurring
        </Text>
        <Text fz="xs" c="gray">
          <Text span fw={600} w="40px" display="inline-block">
            Email:
          </Text>{' '}
          {subscription.donor_email}
        </Text>
        <Text fz="xs" c="gray">
          <Text span fw={600} display="inline-block">
            Donation Amount:
          </Text>{' '}
          {centsToDollar(subscription.price)}
        </Text>
      </SimpleGrid>
    </Card>
  );
};
const CancelSubscriptionModalContent = ({
  onClose,
  subscription,
}: CancelSubscriptionModalContentProps) => {
  const queryClient = useQueryClient();
  const { causeId } = useCrowdfundingProvider();
  const { mutate: cancelSubscription, isPending: isLoading } = useCancelSubscription();

  const handleCancelSubscription = () => {
    cancelSubscription(
      { subscriptionId: subscription.uuid, causeId },
      {
        onSuccess() {
          causeId &&
            queryClient.invalidateQueries({
              queryKey: storeKeys.id(causeId).items.subscriptions.all,
            });
          notifications.show({
            message: `Subscription has been canceled`,
            variant: 'success',
          });
          onClose();
        },
        onError() {
          notifications.show({ message: `Error canceling subscription`, variant: 'error' });
        },
      }
    );
  };

  return (
    <Stack gap="lg">
      <Title order={4}>Would you like to cancel future payments?</Title>
      <RecurringDonationInfoCard subscription={subscription} />
      <Group justify="flex-end">
        <ButtonMantine size="md" variant="subtle" onClick={onClose}>
          Cancel
        </ButtonMantine>
        <ButtonMantine
          size="md"
          color="cerise"
          onClick={handleCancelSubscription}
          loading={isLoading}
        >
          Cancel Recurring Payment
        </ButtonMantine>
      </Group>
    </Stack>
  );
};

const CancelSubscriptionModal = ({
  opened,
  onClose,
  subscription,
}: CancelSubscriptionModalProps) => {
  return (
    <Modal opened={opened} onClose={onClose} title="Cancel Future Payments" size="lg">
      <CancelSubscriptionModalContent onClose={onClose} subscription={subscription} />
    </Modal>
  );
};

export { CancelSubscriptionModal };
